import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Truck, Package, MapPin } from 'lucide-react';
import toast from 'react-hot-toast';
import { deliveriesApi, posApi } from '@/services/api';
import { formatMoney } from '@/utils/currency';
import { format } from 'date-fns';

interface CustomerFulfillmentTabProps {
  customerId: string;
  /** DB fulfillment type: Pickup = Collection UI */
  mode: 'Pickup' | 'Delivery';
  onOpenInvoice?: (invoiceName: string) => void;
}

type CollectionStatus = 'All' | 'Active' | 'Collected';
type DeliveryStatus = 'All' | 'Active' | 'Pending' | 'Out for Delivery' | 'Delivered';

export default function CustomerFulfillmentTab({
  customerId,
  mode,
  onOpenInvoice,
}: CustomerFulfillmentTabProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const isDelivery = mode === 'Delivery';
  const [statusFilter, setStatusFilter] = useState<string>('All');

  const { data, isLoading } = useQuery({
    queryKey: ['customer-fulfillment', customerId, mode, statusFilter],
    queryFn: async () => {
      const res = await posApi.getCustomerFulfillmentOrders({
        customer: customerId,
        fulfillment_type: mode,
        status: statusFilter === 'All' ? undefined : statusFilter,
        page: 1,
        limit: 50,
      });
      return res.data.message as { items: any[]; total: number };
    },
  });

  const updateStatus = useMutation({
    mutationFn: ({ name, status }: { name: string; status: string }) =>
      deliveriesApi.updateStatus(name, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customer-fulfillment', customerId] });
      qc.invalidateQueries({ queryKey: ['customer-dashboard', customerId] });
      toast.success(t('customers.dashboard.statusUpdated'));
    },
    onError: () => toast.error(t('customers.dashboard.statusUpdateFailed')),
  });

  const items = data?.items || [];

  const statusButtons = isDelivery
    ? ([
        { key: 'All' as DeliveryStatus, label: t('customers.dashboard.filterAll') },
        { key: 'Active' as DeliveryStatus, label: t('customers.dashboard.filterOpen') },
        { key: 'Pending' as DeliveryStatus, label: t('customers.dashboard.pending') },
        { key: 'Out for Delivery' as DeliveryStatus, label: t('customers.dashboard.outForDelivery') },
        { key: 'Delivered' as DeliveryStatus, label: t('customers.dashboard.delivered') },
      ])
    : ([
        { key: 'All' as CollectionStatus, label: t('customers.dashboard.filterAll') },
        { key: 'Active' as CollectionStatus, label: t('customers.dashboard.filterOpen') },
        { key: 'Collected' as CollectionStatus, label: t('customers.dashboard.collected') },
      ]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {statusButtons.map((b) => (
          <button
            key={b.key}
            type="button"
            onClick={() => setStatusFilter(b.key)}
            className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border ${
              statusFilter === b.key
                ? 'bg-primary-50 border-primary-300 text-primary-800'
                : 'bg-white border-gray-200 text-gray-600'
            }`}
          >
            {b.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="card p-8 text-center text-gray-400">{t('customers.loading')}</div>
      ) : items.length === 0 ? (
        <div className="card p-8 text-center text-gray-400">
          {isDelivery ? (
            <Truck className="w-10 h-10 mx-auto mb-2 opacity-30" />
          ) : (
            <Package className="w-10 h-10 mx-auto mb-2 opacity-30" />
          )}
          <p>
            {isDelivery
              ? t('customers.dashboard.noDeliveries')
              : t('customers.dashboard.noCollections')}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((row) => (
            <div key={row.name} className="card p-4">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      className="font-semibold text-primary-700 hover:underline text-sm"
                      onClick={() => onOpenInvoice?.(row.name)}
                    >
                      {row.name}
                    </button>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        isDelivery
                          ? 'bg-violet-50 text-violet-700 border border-violet-200'
                          : 'bg-sky-50 text-sky-700 border border-sky-200'
                      }`}
                    >
                      {isDelivery ? (
                        <Truck className="w-3 h-3" />
                      ) : (
                        <Package className="w-3 h-3" />
                      )}
                      {isDelivery
                        ? t('customers.dashboard.delivery')
                        : t('customers.dashboard.collection')}
                    </span>
                    {isDelivery && row.delivery_status && (
                      <span className="text-xs text-gray-600">{row.delivery_status}</span>
                    )}
                    {!isDelivery && (
                      <span className="text-xs text-gray-600">{row.invoice_status}</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {row.invoice_date
                      ? format(new Date(row.invoice_date), 'dd MMM yyyy')
                      : '—'}{' '}
                    · {formatMoney(row.net_total || 0)}
                    {row.balance_amount > 0
                      ? ` · ${t('customers.outstanding')}: ${formatMoney(row.balance_amount)}`
                      : ''}
                  </p>
                  {isDelivery && (row.delivery_address || row.delivery_postal_code) && (
                    <p className="text-xs text-gray-500 mt-1 inline-flex items-start gap-1">
                      <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>
                        {[row.delivery_address, row.delivery_postal_code].filter(Boolean).join(', ')}
                        {row.delivery_phone ? ` · ${row.delivery_phone}` : ''}
                      </span>
                    </p>
                  )}
                  {!isDelivery && (
                    <p className="text-xs text-gray-500 mt-1">
                      {t('customers.dashboard.collectionHint')}
                    </p>
                  )}
                </div>
                {isDelivery &&
                  row.delivery_status !== 'Delivered' &&
                  row.delivery_status !== 'Cancelled' && (
                    <div className="flex flex-wrap gap-1.5 shrink-0">
                      {row.delivery_status === 'Pending' && (
                        <button
                          type="button"
                          className="px-2 py-1 text-xs rounded-lg bg-amber-50 text-amber-800 border border-amber-200"
                          onClick={() =>
                            updateStatus.mutate({ name: row.name, status: 'Out for Delivery' })
                          }
                        >
                          {t('customers.dashboard.markOutForDelivery')}
                        </button>
                      )}
                      <button
                        type="button"
                        className="px-2 py-1 text-xs rounded-lg bg-green-50 text-green-800 border border-green-200"
                        onClick={() => updateStatus.mutate({ name: row.name, status: 'Delivered' })}
                      >
                        {t('customers.dashboard.markDelivered')}
                      </button>
                    </div>
                  )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
